//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useEffect } from 'react';
import styled from '@emotion/styled';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setCurrentTable } from '@/redux/slices/dataTableSlice';
import { selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import { DataTableConfig } from '@/__configs/config_types';
import TableContainer from '@/components/_common/Table/TableContainer';
import AvailsScheduleTitleBar from '@/components/Focus/AvailsSchedule/AvailsScheduleTitleBar';
import TitleNavigationDrawer from '@/components/Focus/AvailsSchedule/TitleNavigationDrawer/TitleNavigationDrawer';
import FiltersContainer from '@/components/Focus/common/FiltersContainer';
import DataTableFilters from '@/components/_common/Filters/DataTableFilters';

interface DataTableLayoutProps {
  tableKey: string;
  onRowClick?: (e: React.MouseEvent, item: any) => void;
  onReorderRows?: (taskId: number, priority: number) => Promise<void>;
  simplified?: boolean;
  activeRowId?: number;
  children?: React.ReactNode;
}

const DataTableLayout = ({ tableKey, onRowClick, onReorderRows, simplified, activeRowId, children }: DataTableLayoutProps) => {
  const dispatch = useAppDispatch();
  const currentTableKey = useAppSelector(selectCurrentTableKey);

  useEffect(() => {
    dispatch(setCurrentTable(tableKey as keyof DataTableConfig));
  }, []);

  if (tableKey === 'focusSchedule')
    return (
      <>
        <AvailsScheduleTitleBar />
        <SchedulesWrapper>
          <TitleNavigationDrawer />
          <TableWrapper>
            {currentTableKey === tableKey && (
              <>
                <FiltersContainer>
                  <DataTableFilters disableExportReport />
                </FiltersContainer>
                <TableContainer
                  // used to show a simplified table without the filters, see JobDetails Tasks tab
                  simplified={simplified}
                  onRowClick={onRowClick}
                  onReorderRows={onReorderRows}
                  activeRowId={activeRowId}
                />
              </>
            )}
          </TableWrapper>
        </SchedulesWrapper>
      </>
    );

  return (
    <>
      {children}
      {currentTableKey === tableKey && (
        <TableContainer
          // used to show a simplified table without the filters, see JobDetails Tasks tab
          simplified={simplified}
          onRowClick={onRowClick}
          onReorderRows={onReorderRows}
          activeRowId={activeRowId}
        />
      )}
    </>
  );
};

export default DataTableLayout;

const SchedulesWrapper = styled.div(() => ({
  display: 'flex',
  width: '100%',
  height: '100%'
}));

const TableWrapper = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'auto'
}));
